import { Controller } from "@hotwired/stimulus"
import { debug } from "../debug"
import { marked } from "../initializers/marked"
import DOMPurify from "dompurify"

// Configure DOMPurify to allow YouTube iframes and code blocks while maintaining security
const purifyConfig = {
  ADD_TAGS: ['iframe', 'div', 'a', 'pre', 'code'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src', 'style', 'width', 'height', 'title', 'class', 'href', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
  // Allow ALL legitimate protocols - https, http, mailto, tel, whatsapp
  // This blocks javascript: and data: URIs which are the actual XSS vectors
  ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp|mailto|tel|whatsapp):)|^(?:www\.)|^(?:\/\/)/
};

// Connects to data-controller="inline-chat"
export default class extends Controller {
  static targets = ["input", "chatWindow", "messages", "sendButton"]
  
  connect() {
    debug("%c🔒 SECURE CHAT v3.0 - SERVER-MANAGED SESSIONS 🔒", "background: #FF5722; color: white; padding: 8px; border-radius: 4px; font-weight: bold; font-size: 14px;");
    debug("🔒🔒🔒 SECURE INLINE CHAT - NO CLIENT SESSION CONTROL - " + new Date().toISOString());
    
    // Load session storage and show chat window if messages exist (DESKTOP ONLY)
    if (!this.isMobile()) {
      this.loadMessagesFromSession()
      
      // IMMEDIATELY after loading, check if we have messages and show chat
      if (this.hasMessagesTarget && this.messagesTarget.children.length > 0) {
        debug('🔥 Desktop: Loaded', this.messagesTarget.children.length, 'messages from session, showing chat window')
        
        // Mark chat as active since we have messages
        this.chatActive = true
        
        // Show the chat window
        this.showChatWindow()
      } else {
        debug('🔥 Desktop: No messages in session storage, chat window stays hidden')
      }
    }
    
    // SECURITY: Remove localStorage session management - now server-controlled
    window.chatSessionIds = window.chatSessionIds || {};
    
    // Log initial chat window state for debugging
    if (this.hasChatWindowTarget) {
      const computedStyle = window.getComputedStyle(this.chatWindowTarget)
      debug('🔥 Initial chat window display:', computedStyle.display)
      debug('🔥 Initial chat window classes:', this.chatWindowTarget.className)
      debug('🔥 Chat window element:', this.chatWindowTarget)
    }
    // SECURITY: No longer initialize session_id from localStorage
    
    // CSS handles height dynamically - no resize listeners needed
    
    // Using relative URL for backend proxy
    this.source = 'rails_front'
    
    // Initialize basic chat properties
    this.chatActive = false;
    this.messageCount = 0;
    
    // Add event listener for input focus to hide placeholder
    // and set a flag that user has interacted.
    this.inputFocusedOnce = false;
    this.inputTarget.addEventListener('focus', this._handleInputFocus.bind(this));
    this.inputTarget.addEventListener('blur', this._handleInputBlur.bind(this));

    // Bind and add resize listener for dynamic placeholders
    this.boundUpdatePlaceholder = this._updateDynamicPlaceholder.bind(this);
    window.addEventListener('resize', this.boundUpdatePlaceholder);
    this._updateDynamicPlaceholder(); // Initial call
    
    // MOBILE ONLY: Set up mobile functions (ERB creates the modal)
    if (this.isMobile()) {
      debug('🔥 Stimulus: Mobile device - setting up functions (ERB handles modal)')
      this.mobileModalVisible = false;
      
      // DON'T create modal - ERB creates it
      // this.createMobileModal();
      
      // Set up mobile-specific functions globally - MOBILE ONLY
      window.mobileChatFunctions = {
        open: this.openMobileChat.bind(this),
        close: this.closeMobileChat.bind(this),
        send: this.sendMessageFromMobile.bind(this),
        isMobile: this.isMobile.bind(this)
      };
      
      // Set up intercept of chat actions on mobile
      this.interceptMobileChatActions();
    } else {
      debug('🔥 Stimulus: Desktop device - skipping mobile modal functionality')
      // Ensure desktop NEVER has mobile functions
      this.openMobileChat = null;
      this.closeMobileChat = null;
      this.sendMessageFromMobile = null;
    }
    
    // Initialize any other necessary setup
    this.setupEventListeners();
  }

  // Check if on mobile device
  isMobile() {
    return window.matchMedia('(max-width: 768px)').matches;
  }
  
  // Handle form submission
  send(event) {
    if (event) event.preventDefault();
    
    // First check if we should redirect to mobile on mobile devices
    const message = this.inputTarget.value.trim();
    if (!message) return;
    
    debug("[InlineChatController] Send called.");
    
    // If on mobile and not already in mobile modal, open mobile modal after sending
    if (this.isMobile() && !this.mobileModalVisible && window.mobileChatFunctions) {
      debug("🔥 Mobile: Will open modal after sending inline message");
      
      // Let this regular send proceed, then open mobile modal after
      setTimeout(() => {
        window.mobileChatFunctions.open();
      }, 300);
    }
    
    this.inputFocusedOnce = true; // Assume send means user interacted
    
    // Change button text after first message
    if (this.messageCount === 0) {
      // Get current language
      const currentLang = document.documentElement.lang || 'hr';
      
      // Change button text based on language - corrected Croatian translation
      const buttonText = currentLang === 'en' ? 'Send' : 'Pošalji';
      
      // Force button text update with direct DOM manipulation
      if (this.hasSendButtonTarget) {
        this.sendButtonTarget.textContent = buttonText;
        debug('Button text changed to:', buttonText);
      }
    }
    
    // Increment message count
    this.messageCount++;
    
    // Show the chat window if it's not already visible
    if (!this.chatActive) {
      this.chatWindowTarget.style.display = 'block';
      this.chatActive = true;
    } else {
      // Make sure chat window is visible
      this.showChatWindow()
    }
    
    // Show chat window if not already visible and adjust height
    this.showChatWindow()
    
    // Add user message
    this.addMessage(message, true)
    
    // Clear input
    this.inputTarget.value = ''
    
    // Add loading indicator
    const loadingDiv = document.createElement('div')
    loadingDiv.className = 'chat-loading'
    loadingDiv.style.margin = '0 0 16px 0'
    loadingDiv.style.padding = '12px 16px'
    loadingDiv.style.borderRadius = '8px'
    loadingDiv.style.maxWidth = '80%'
    loadingDiv.style.backgroundColor = '#f0f0f0'
    loadingDiv.style.marginRight = 'auto'
    loadingDiv.style.textAlign = 'left'
    loadingDiv.style.fontStyle = 'italic'
    loadingDiv.style.color = '#666'
    loadingDiv.style.display = 'flex'
    loadingDiv.style.alignItems = 'center'
    loadingDiv.style.gap = '8px'
    
    // Create loading spinner
    const spinner = document.createElement('div')
    spinner.className = 'loading-spinner'
    spinner.style.width = '16px'
    spinner.style.height = '16px'
    spinner.style.border = '2px solid #ccc'
    spinner.style.borderTopColor = '#666'
    spinner.style.borderRadius = '50%'
    spinner.style.animation = 'spin 1s linear infinite'
    
    // @keyframes spin lives in _inline_chat.scss (a script-injected <style> has no nonce and is blocked by the CSP)
    
    // Add spinner and text to loading div
    loadingDiv.appendChild(document.createTextNode('Fiumed AI'))
    loadingDiv.appendChild(spinner)
    
    this.messagesTarget.appendChild(loadingDiv)
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    
    // SECURE API request - server manages session tokens
    fetch('/api/v1/secure_chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify({
        message: message
        // SECURITY: No session_id sent - server manages this securely
      })
    })
    .then(async response => {
      // Remove loading indicator
      if (loadingDiv.parentNode) {
        loadingDiv.remove()
      }
      
      const data = await response.json()
      
      // Handle both success and error responses (check if error field exists)
      if (data.error) {
        // Display the bilingual error message from API (Rails or Flask)
        this.addMessage(data.error, false)
        
        if (this.mobileModalVisible) {
          this.updateMobileChat(data.error, false);
        }
        return;
      }
      
      // SECURITY: No longer save session_id to localStorage
      // Server manages all session tokens securely
      debug('🔒 Secure API response received')
      
      // Add AI response
      this.addMessage(data.response || 'Sorry, no response received', false)
      
      // If we're in mobile mode, also update the mobile modal
      if (this.mobileModalVisible) {
        this.updateMobileChat(data.response || 'Sorry, no response received', false);
      }
    })
    .catch(error => {
      console.error('API Error:', error);
      
      // Remove loading indicator
      if (loadingDiv.parentNode) {
        loadingDiv.remove();
      }
      
      // Add error message for network errors
      this.addMessage('Failed to connect to the chat service.', false);
      
      if (this.mobileModalVisible) {
        this.updateMobileChat('Failed to connect to the chat service.', false);
      }
    })
  }
  
  // Create the mobile chat modal
  createMobileModal() {
    debug('Creating mobile chat modal');
    
    // Don't create duplicate modals
    if (document.getElementById('mobileChatModal')) return;
    
    // Create modal container
    const modal = document.createElement('div');
    modal.id = 'mobileChatModal';
    modal.className = 'mobileChatModal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      background-color: white;
      z-index: 2147483647;
      display: none;
      flex-direction: column;
    `;
    
    // Check language
    const lang = document.documentElement.lang || 'hr';
    const isEnglish = lang === 'en';
    
    // Create header
    const header = document.createElement('div');
    header.className = 'modal-header';
    header.style.cssText = `
      padding: 16px;
      border-bottom: 1px solid #e1e1e1;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
    
    const title = document.createElement('h3');
    title.className = 'modal-title';
    title.textContent = isEnglish ? 'Chat' : 'Chat';
    title.style.margin = '0';
    
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = `
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      padding: 0 8px;
    `;
    closeBtn.addEventListener('click', () => this.closeMobileChat());
    
    header.appendChild(title);
    header.appendChild(closeBtn);
    
    // Create body
    const body = document.createElement('div');
    body.className = 'modal-body';
    body.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 16px;
    `;
    
    const messages = document.createElement('div');
    messages.id = 'mobileChatMessages';
    messages.className = 'chat-messages';
    body.appendChild(messages);
    
    // Create footer with input
    const footer = document.createElement('div');
    footer.className = 'modal-footer';
    footer.style.cssText = `
      padding: 16px;
      border-top: 1px solid #e1e1e1;
      background-color: #f8f8f8;
    `;
    
    const form = document.createElement('form');
    form.className = 'modal-form';
    form.style.cssText = `
      display: flex;
      gap: 8px;
    `;
    form.addEventListener('submit', (e) => { 
      e.preventDefault();
      return false;
    });
    
    const textarea = document.createElement('textarea');
    textarea.id = 'mobileChatInput';
    textarea.className = 'search-input chat-textarea';
    textarea.placeholder = isEnglish ? 'Type your message...' : 'Upišite poruku...';
    textarea.style.cssText = `
      flex: 1;
      padding: 10px;
      border-radius: 18px;
      border: 1px solid #ccc;
      resize: none;
      min-height: 40px;
      max-height: 120px;
    `;
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessageFromMobile();
        return false;
      }
      return true;
    });
    
    const sendBtn = document.createElement('button');
    sendBtn.id = 'mobile-send-button';
    sendBtn.className = 'search-button';
    sendBtn.textContent = isEnglish ? 'Send' : 'Pošalji';
    sendBtn.style.cssText = `
      min-width: 60px;
      border-radius: 18px;
      padding: 8px 16px;
      background-color: #5a9cd8;
      color: white;
      border: none;
      cursor: pointer;
    `;
    sendBtn.addEventListener('click', () => this.sendMessageFromMobile());
    
    form.appendChild(textarea);
    form.appendChild(sendBtn);
    footer.appendChild(form);
    
    // Assemble modal
    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    
    // Add to body
    document.body.appendChild(modal);
    
    debug('Mobile modal created and added to DOM');
  }
  
  // Open the mobile chat modal
  openMobileChat() {
    debug('%c Opening mobile chat', 'background: #4ecdc4; color: white; padding: 2px 4px;');
    
    if (this.mobileModalVisible) return;
    
    // Ensure modal exists
    const modal = document.getElementById('mobileChatModal');
    if (!modal) {
      console.error('Mobile modal not found, creating it now');
      this.createMobileModal();
      return setTimeout(() => this.openMobileChat(), 100);
    }
    
    // Get the mobile messages container
    const mobileMessages = document.getElementById('mobileChatMessages');
    if (!mobileMessages) {
      console.error('Mobile messages container not found');
      return;
    }
    
    // Copy messages from inline chat
    if (this.hasMessagesTarget) {
      mobileMessages.innerHTML = this.messagesTarget.innerHTML;
    }
    
    // Hide the inline chat window
    if (this.hasChatWindowTarget) {
      this.chatWindowTarget.style.display = 'none';
    }
    
    // Show modal with max z-index
    modal.style.display = 'flex';
    modal.style.zIndex = '2147483647';
    this.mobileModalVisible = true;
    
    // Scroll to bottom
    mobileMessages.scrollTop = mobileMessages.scrollHeight;
    
    // Focus on input and transfer any existing text
    setTimeout(() => {
      const mobileInput = document.getElementById('mobileChatInput');
      
      if (mobileInput) {
        if (this.hasInputTarget && this.inputTarget.value) {
          mobileInput.value = this.inputTarget.value;
          this.inputTarget.value = '';
        }
        mobileInput.focus();
      }
    }, 100);
  }
  
  // Close the mobile chat modal
  closeMobileChat() {
    debug('🔥 Closing mobile chat - keeping desktop inline chat hidden')
    
    if (!this.mobileModalVisible) return;
    
    const modal = document.getElementById('mobileChatModal');
    const mobileMessages = document.getElementById('mobileChatMessages');
    
    // Hide modal
    if (modal) {
      modal.style.display = 'none';
    }
    
    // ❌ REMOVED: Don't show desktop inline chat on mobile devices
    // This was the source of the mobile bleed-through issue
    // Mobile should only show: input field + floating AI button
    
    // Transfer messages back to inline chat to keep in sync (for desktop when needed)
    if (this.hasMessagesTarget && mobileMessages) {
      this.messagesTarget.innerHTML = mobileMessages.innerHTML;
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    }
    
    this.mobileModalVisible = false;
    
    // Focus on inline input after closing modal
    if (this.hasInputTarget) {
      this.inputTarget.focus();
    }
  }
  
  // Send a message from the mobile modal
  sendMessageFromMobile() {
    debug('Sending message from mobile chat modal');
    
    const mobileInput = document.getElementById('mobileChatInput');
    if (!mobileInput || !mobileInput.value.trim()) return;
    
    const message = mobileInput.value.trim();
    mobileInput.value = '';
    
    // Add user message to mobile chat
    this.updateMobileChat(message, true);
    
    // Add loading indicator to mobile chat
    const loadingEl = this.addMobileLoadingIndicator();
    
    // SECURITY: No longer get session_id from localStorage
    // Server manages all session tokens securely
    
    // Prepare SECURE API request
    const payload = {
      message: message
      // SECURITY: No session_id - server handles this
    };
    
    // Send SECURE API request
    debug('🔒 Sending secure message to API:', message);
    
    fetch('/api/v1/secure_chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json()
      
      // Remove loading indicator
      if (loadingEl && loadingEl.parentNode) {
        loadingEl.remove();
      }
      
      // Check if the response contains an error (even with 200 status)
      if (data.error) {
        // Display the bilingual error message from Flask API
        this.updateMobileChat(data.error, false);
        
        // Also update the inline chat (this will be shown when modal closes)
        if (this.hasMessagesTarget) {
          // First add the user message
          this.addMessage(message, true);
          // Then the error message
          this.addMessage(data.error, false);
        }
        return;
      }
      
      // SECURITY: No longer save session_id to localStorage
      // Server manages all session tokens securely
      debug('🔒 Secure mobile API response received')
      
      // Add AI response to mobile chat
      this.updateMobileChat(data.response || 'Sorry, no response received', false);
      
      // Also update the inline chat (this will be shown when modal closes)
      if (this.hasMessagesTarget) {
        // First add the user message
        this.addMessage(message, true);
        // Then the response
        this.addMessage(data.response || 'Sorry, no response received', false);
      }
    })
    .catch(async error => {
      console.error('Error with chat API:', error);
      
      let errorMessage = 'Sorry, there was an error processing your request.';
      
      // Try to extract error message from HTTP error response
      if (error.response && error.response.body) {
        try {
          const errorData = JSON.parse(error.response.body);
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
      }
      
      // Remove loading indicator
      if (loadingEl && loadingEl.parentNode) {
        loadingEl.remove();
      }
      
      // Add error message
      this.updateMobileChat(errorMessage, false);
    });
  }
  
  // Add a message to the mobile chat
  updateMobileChat(text, isUser) {
    const mobileMessages = document.getElementById('mobileChatMessages');
    if (!mobileMessages) return;
    
    const msgEl = document.createElement('div');
    msgEl.className = isUser ? 'user-message' : 'ai-message';
    msgEl.style.marginBottom = '16px';
    msgEl.style.padding = '12px 16px';
    msgEl.style.borderRadius = '8px';
    msgEl.style.maxWidth = '80%';
    
    if (isUser) {
      msgEl.style.backgroundColor = '#D7E5EE';
      msgEl.style.marginLeft = 'auto';
      msgEl.style.textAlign = 'right';
      msgEl.textContent = text;
    } else {
      msgEl.style.backgroundColor = '#f0f0f0';
      msgEl.style.marginRight = 'auto';
      msgEl.style.textAlign = 'left';
      
      // Apply markdown rendering if available
      try {
        // marked is already configured with custom tokenizer in initializers/marked.js
        const htmlContent = marked.parse(text);
        
        // Then process YouTube links in the HTML output
        const processedHtml = this.processYouTubeLinks(htmlContent);
        
        // ALWAYS sanitize with DOMPurify (it will keep YouTube iframes due to purifyConfig)
        msgEl.innerHTML = DOMPurify.sanitize(processedHtml, purifyConfig);
      } catch (e) {
        console.error('Error rendering markdown:', e);
        msgEl.textContent = text;
      }
    }
    
    mobileMessages.appendChild(msgEl);
    mobileMessages.scrollTop = mobileMessages.scrollHeight;
  }
  
  // Add a loading indicator to the mobile chat
  addMobileLoadingIndicator() {
    const container = document.getElementById('mobileChatMessages');
    if (!container) return null;
    
    const indicator = document.createElement('div');
    indicator.className = 'loading-indicator';
    indicator.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      padding: 12px 16px;
      background-color: #f0f0f0;
      border-radius: 8px;
      font-style: italic;
      color: #666;
      max-width: 80%;
      margin-right: auto;
    `;
    
    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width: 16px;
      height: 16px;
      border: 2px solid #ccc;
      border-top-color: #666;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    `;
    
    indicator.appendChild(document.createTextNode('Fiumed AI'));
    indicator.appendChild(spinner);
    
    container.appendChild(indicator);
    container.scrollTop = container.scrollHeight;
    
    return indicator;
  }
  
  // Set up event listeners for mobile chat actions
  interceptMobileChatActions() {
    debug('Setting up mobile chat action interception');
    
    // Set up listener for test button clicks
    document.addEventListener('click', (e) => {
      if (e.target && e.target.id === 'mobileChatTestButton') {
        debug('Test button clicked');
        this.openMobileChat();
      }
    });
  }
  
  // Add message to chat with modern auto-scroll behavior
  addMessage(text, isUser = false) {
    if (!this.hasMessagesTarget) return
    
    const messageDiv = document.createElement('div')
    const role = isUser ? 'user' : 'ai'
    messageDiv.className = isUser ? 'user-message' : 'ai-message'
    messageDiv.dataset.role = role
    
    if (isUser) {
      messageDiv.textContent = text
      debug('🔥 Adding user message, will scroll to bottom')
    } else {
      debug('🔥 Adding AI message, will scroll to show it')
      // Apply markdown rendering if available
      try {
        // marked is already configured with custom tokenizer in initializers/marked.js
        const htmlContent = marked.parse(text)
        
        // Then process YouTube links in the HTML output
        const processedHtml = this.processYouTubeLinks(htmlContent)
        
        // ALWAYS sanitize with DOMPurify (it will keep YouTube iframes due to purifyConfig)
        messageDiv.innerHTML = DOMPurify.sanitize(processedHtml, purifyConfig)
      } catch (e) {
        console.error('Markdown parsing error:', e);
        messageDiv.textContent = text
      }
    }
    
    this.messagesTarget.appendChild(messageDiv)
    
    // Show chat window first
    this.showChatWindow()
    
    // Save to session storage (proper Rails way)
    this.saveMessagesToSession()
    
    // Modern auto-scroll behavior like ChatGPT/Claude
    this.autoScrollToMessage(messageDiv, isUser)
  }
  
  // Modern auto-scroll behavior for chat messages (DESKTOP ONLY)
  autoScrollToMessage(messageElement, isUser) {
    if (!this.hasChatWindowTarget || !this.hasMessagesTarget) return
    
    // Mobile devices use separate fullscreen modal with their own scrolling
    if (this.isMobile()) {
      debug('🔥 Mobile device detected - skipping desktop auto-scroll (mobile handles own scrolling)')
      return
    }
    
    const chatWindow = this.chatWindowTarget
    
    // Small delay to ensure DOM has updated and chat window is visible
    requestAnimationFrame(() => {
      if (isUser) {
        // For user messages: scroll to bottom to show full user message
        debug('🔥 Auto-scrolling to bottom for user message')
        debug('🔥 Chat window scroll height:', chatWindow.scrollHeight)
        
        chatWindow.scrollTo({
          top: chatWindow.scrollHeight,
          behavior: 'smooth'
        })
      } else {
        // For AI messages: scroll to the beginning of the AI response  
        debug('🔥 Auto-scrolling to show AI response at top')
        
        // Get the position of the AI message within the chat window
        const messageRect = messageElement.getBoundingClientRect()
        const chatRect = chatWindow.getBoundingClientRect()
        
        // Calculate scroll position to show AI message at top with padding
        const currentScroll = chatWindow.scrollTop
        const messageOffset = messageRect.top - chatRect.top
        const targetScroll = currentScroll + messageOffset - 20 // 20px padding from top
        
        debug('🔥 Message offset:', messageOffset, 'Target scroll:', targetScroll)
        
        chatWindow.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth'
        })
      }
    })
  }
  
  // Show the chat window - DESKTOP ONLY (mobile uses separate fullscreen modal)
  showChatWindow() {
    debug('🔥 showChatWindow() called - FIXING DESKTOP RETURN ISSUE!')
    
    // Mobile devices use separate fullscreen modal - don't show desktop inline chat
    if (this.isMobile()) {
      debug('🔥 Mobile device detected - not showing desktop inline chat (mobile uses fullscreen modal)')
      return
    }
    
    // Desktop only: FORCE show inline chat with height expansion
    if (this.hasChatWindowTarget) {
      debug('🔥 Desktop device - FORCING chat window visibility')
      
      // Add visible class
      this.chatWindowTarget.classList.add('visible')
      
      // ALSO force display style as backup
      this.chatWindowTarget.style.display = 'flex'
      
      // Log the computed styles to verify CSS is working
      const computedStyle = window.getComputedStyle(this.chatWindowTarget)
      debug('🔥 Chat window display after fixes:', computedStyle.display)
      debug('🔥 Chat window height after fixes:', computedStyle.height)
      debug('🔥 Chat window has visible class:', this.chatWindowTarget.classList.contains('visible'))
      
      debug('🔥 DESKTOP CHAT WINDOW SHOULD NOW BE VISIBLE!')
    } else {
      console.error('🔥 NO chatWindowTarget found!')
    }
  }
  
  // Process YouTube links to embed videos (works on HTML content after markdown parsing)
  processYouTubeLinks(htmlContent) {
    // Handle YouTube links in href attributes (from markdown [text](url) which becomes <a href="url">text</a>)
    const linkRegex = /<a[^>]*href="((?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})[^"]*)"[^>]*>.*?<\/a>/g;
    
    htmlContent = htmlContent.replace(linkRegex, (match, url, videoId) => {
      return `<div class="youtube-embed">
        <iframe 
          src="https://www.youtube.com/embed/${videoId}" 
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
          allowfullscreen>
        </iframe>
      </div>`;
    });
    
    // Also handle plain YouTube URLs in text (that aren't already in links)
    const plainUrlRegex = /(?<!href="|src=")(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^\s<]*)?/g;
    
    return htmlContent.replace(plainUrlRegex, (match, videoId) => {
      return `<div class="youtube-embed">
        <iframe 
          src="https://www.youtube.com/embed/${videoId}" 
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
          allowfullscreen>
        </iframe>
      </div>`;
    });
  }

  _handleInputFocus() {
    this.inputFocusedOnce = true;
    this.inputTarget.setAttribute('placeholder', '');
  }

  _handleInputBlur() {
    // If input is empty and wasn't focused before, re-apply dynamic placeholder
    if (this.inputTarget.value.trim() === '') {
        this._updateDynamicPlaceholder();
    }
  }

  _updateDynamicPlaceholder() {
    if (!this.hasInputTarget) return;

    // Only change placeholder if the input is not actively focused
    // OR if it is focused but empty and the user hasn't typed anything yet (optional)
    // For simplicity, let's only update if not the active element OR if it's empty.
    if (document.activeElement !== this.inputTarget || this.inputTarget.value.trim() === '') {
      const lang = document.documentElement.lang || 'hr';
      if (window.innerWidth <= 768) {
        // Mobile placeholders
        this.inputTarget.placeholder = lang === 'en' ? 'What do you want to know?' : 'Što vas zanima?';
      } else {
        // Desktop placeholders
        this.inputTarget.placeholder = lang === 'en' 
          ? 'Ask and get an answer right away...' 
          : 'Napišite što vas zanima i odgovor stiže odmah...';
      }
    }
  }
  
  setupEventListeners() {
    // Additional event listeners if needed
  }

  // Proper Rails session storage - DHH approved
  saveMessagesToSession() {
    try {
        const messages = Array.from(this.messagesTarget.children).map(child => {
          const role = child.dataset.role || (child.classList.contains('user-message') ? 'user' : 'ai')
          return {
            text: child.textContent,
            isUser: role === 'user',
            role,
            html: child.innerHTML
          }
        })
      sessionStorage.setItem('homepage_chat', JSON.stringify(messages))
      debug('💾 Stimulus: Saved', messages.length, 'messages')
    } catch (e) {
      debug('💾 Stimulus: Save failed:', e)
    }
  }

  loadMessagesFromSession() {
    try {
      const stored = sessionStorage.getItem('homepage_chat')
      if (stored) {
        const messages = JSON.parse(stored)
        debug('💾 Stimulus: Loading', messages.length, 'messages')
        this.messagesTarget.innerHTML = ''
        messages.forEach(msg => {
          const div = document.createElement('div')
          const role = msg.role || (msg.isUser ? 'user' : 'ai')
          div.className = role === 'user' ? 'user-message' : 'ai-message'
          div.dataset.role = role
          div.innerHTML = DOMPurify.sanitize(msg.html, purifyConfig)
          this.messagesTarget.appendChild(div)
        })
      }
    } catch (e) {
      debug('💾 Stimulus: Load failed:', e)
    }
  }

  disconnect() {
    // Remove resize listener for placeholder updates
    window.removeEventListener('resize', this.boundUpdatePlaceholder);
  }
  
}
